import React, { useState, useEffect } from 'react';
import Toast from '../../components/Toast';
import { formatDateTimeSriLanka } from '../../utils/dateUtils';
import './EditOrderModal.css';

function EditOrderModal({ order, isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phone1: '',
    phone2: '',
    address: '',
    area: '',
    status: 'pending',
    deliveryType: '',
    deliveryDate: '',
    paymentMethod: 'Cash on Delivery',
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (order && isOpen) {
      setFormData({
        customerName: order.customerName || '',
        email: order.email || '',
        phone1: order.phone1 || '',
        phone2: order.phone2 || '',
        address: order.address || '',
        area: order.area || '',
        status: (order.status || 'pending').toLowerCase(),
        deliveryType: order.deliveryType || '',
        deliveryDate: order.deliveryDate ? order.deliveryDate.split('T')[0] : '',
        paymentMethod: order.paymentMethod || 'Cash on Delivery',
      });
    }
  }, [order, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.customerName.trim()) {
      setToast({ message: 'Customer name is required', type: 'error' });
      return;
    }
    if (!formData.phone1.trim()) {
      setToast({ message: 'Phone 1 is required', type: 'error' });
      return;
    }
    if (!formData.address.trim()) {
      setToast({ message: 'Address is required', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...formData,
        deliveryDate: formData.deliveryDate ? new Date(formData.deliveryDate).toISOString() : order.deliveryDate,
        // Preserve pricing information from original order
        subtotalPrice: order.subtotalPrice || order.totalPrice || 0,
        deliveryCharge: order.deliveryCharge || 0,
        totalPrice: order.totalPrice || 0,
      });
      setToast({ message: 'Order updated successfully!', type: 'success' });
      onClose();
    } catch (error) {
      console.error('Error saving order:', error);
      setToast({ message: 'Failed to update order', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Order #{order?.id}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customerName">Customer Name *</label>
              <input
                type="text"
                id="customerName"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                placeholder="Enter customer name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone1">Phone 1 *</label>
              <input
                type="tel"
                id="phone1"
                name="phone1"
                value={formData.phone1}
                onChange={handleChange}
                placeholder="Enter primary phone"
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone2">Phone 2</label>
              <input
                type="tel"
                id="phone2"
                name="phone2"
                value={formData.phone2}
                onChange={handleChange}
                placeholder="Enter secondary phone (optional)"
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="address">Address *</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter delivery address"
              rows="3"
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="area">Delivery Area *</label>
            <select
              id="area"
              name="area"
              value={formData.area}
              onChange={handleChange}
              required
            >
              <option value="">Select delivery area</option>
              <option value="Gampaha District">Gampaha District</option>
              <option value="Negombo">Negombo</option>
              <option value="Kalutara">Kalutara</option>
              <option value="Panadura">Panadura</option>
              <option value="Badulla Town">Badulla Town</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">Status *</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="deliveryType">Delivery Type</label>
              <input
                type="text"
                id="deliveryType"
                name="deliveryType"
                value={formData.deliveryType}
                onChange={handleChange}
                placeholder="e.g., Standard, Express"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="deliveryDate">Delivery Date</label>
            <input
              type="date"
              id="deliveryDate"
              name="deliveryDate"
              value={formData.deliveryDate}
              onChange={handleChange}
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="paymentMethod">Payment Method</label>
            <select
              id="paymentMethod"
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleChange}
            >
              <option value="Cash on Delivery">Cash on Delivery</option>
              <option value="Card Payment">Card Payment</option>
            </select>
          </div>

          {order?.completedAt && (
            <div className="form-group">
              <label>Completed Date</label>
              <div className="readonly-field">
                {formatDateTimeSriLanka(order.completedAt)}
              </div>
            </div>
          )}

          {order?.items && order.items.length > 0 && (
            <div className="items-section">
              <h3>Order Items</h3>
              <div className="items-list">
                {order.items.map((item, idx) => (
                  <div key={idx} className="item-display">
                    <span className="item-product">{item.productName}</span>
                    <span className="item-qty">Qty: {item.quantity}</span>
                    <span className="item-price">Rs. {(item.price || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              
              {/* Order Summary */}
              <div className="order-summary">
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>Rs. {(order.subtotalPrice || order.totalPrice || 0).toLocaleString()}</span>
                </div>
                {order.deliveryCharge !== undefined && (
                  <div className="summary-row">
                    <span>Delivery Charge:</span>
                    <span className="delivery-charge">Rs. {order.deliveryCharge.toLocaleString()}</span>
                  </div>
                )}
                <div className="summary-row total">
                  <strong>Order Total:</strong>
                  <strong>Rs. {(order.totalPrice || 0).toLocaleString()}</strong>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditOrderModal;
